import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { Button, Card, Col, Container, Row, Spinner } from 'react-bootstrap';
import { Form, Formik } from 'formik';
import { FORM_MODE, Permissions } from '@awp/constants';
import { isValueEmpty } from '@awp/helpers/utilsReact';
import AwpValidationErrorModal from '@awp/components/Common/AwpValidationErrorModal';
import { getAvalancheGeographicAreas } from '@awp/stores/avalancheSlice';
import Authorize from '@awp/components/Authorize';
import AwpCloseCancelButtons from '../../components/AwpCloseCancelButton';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import {
  AVALANCHE_PATH_PARAMS,
  AVALANCHE_PATHS_DEFAULT_DTO,
  AvalanchePathsService,
} from '@components/AvalanchePathsAdmin/AvalanchePathsService';
import { AwpContainer } from '@components/Common/AwpContainer';
import { getListPageParams } from '@awp/helpers/navigation';

const AvalanchePathsView = (props) => {
  const navigate = useNavigate();
  const { id: entityId } = useParams();
  const listPageURL = getListPageParams();
  const [searchParams, setSearchParams] = useSearchParams();

  const programAreas = useSelector((state) => getAvalancheGeographicAreas(state, 'AVALANCHE PROGRAM'));
  const avalancheList = useSelector((state) => getAvalancheGeographicAreas(state, 'AVALANCHE AREA'));

  const [area, setArea] = useState({});
  const [areaList, setAreaList] = useState(avalancheList);
  const [loading, setLoading] = useState(false);
  const [entityData, setEntityData] = useState({});
  const [entityErrors, setEntityErrors] = useState(null);
  const [tabKey, setTabKey] = useState(searchParams.get(AVALANCHE_PATH_PARAMS.TAB_KEY) ?? undefined);

  const avalanchePrograms = programAreas;
  const avalanchePathsService = new AvalanchePathsService(FORM_MODE.VIEW);
  const { readOnly, title, relativePath, mode, entityName } = avalanchePathsService.crud;

  useEffect(() => {
    const avalancheProgram = avalanchePrograms.find((x) => x.value === area.parentId);
    if (avalancheProgram) {
      const areaListFilterCriteria = (x) => x.parentId === avalancheProgram.value;
      setAreaList(avalancheList.filter(areaListFilterCriteria).sort((a, b) => a.name.localeCompare(b.name)));
    }
    loadAvalanchePath();
  }, [entityId]);

  const loadAvalanchePath = async () => {
    setLoading(true);

    return AvalanchePathsService.loadAvalanchePath(entityId).then(async (record) => {
      if (isValueEmpty(record)) {
        setEntityData({});
      } else {
        setEntityData({
          metaFields: AVALANCHE_PATHS_DEFAULT_DTO.metaFields,
          ...record,
        });
      }

      setLoading(false);
    });
  };

  const onTabSelect = (event) => {
    setTabKey(event);

    avalanchePathsService.setTabSearchParams(setSearchParams, event);
  };

  const onClickCancel = () => {
    navigate(`${relativePath}${listPageURL}`);
  };

  const onEditButtonClick = () => {
    navigate(`${relativePath}${FORM_MODE.EDIT}/${entityId}`);
  };

  return (
    <>
      <AwpContainer>
        {loading ? (
          <Container>
            <Spinner animation="border" />
          </Container>
        ) : (
          <>
            <Formik
              validateOnMount={true}
              initialValues={{
                ...avalanchePathsService.mapInitialValues({ entityData, avalanchePrograms, areaList }),
              }}
            >
              {(props) => (
                <Form>
                  <Link className="awp-text-btn" to={`${relativePath}${listPageURL}`}>
                    {`List ${entityName}`}
                  </Link>
                  <span className="mx-3">&gt;</span>
                  <span>{title}</span>
                  <Card border="light" className="page-card">
                    <Card.Header>
                      <Row className="mt-4"></Row>
                      {avalanchePathsService.crud.getHeader()}
                    </Card.Header>
                    <Card.Body>
                      {avalanchePathsService.getFields({
                        entityData,
                        readOnly,
                        avalanchePrograms,
                        areaList,
                        loading,
                        props,
                        mode,
                        onTabSelect,
                      })}
                    </Card.Body>
                    <Card.Footer>
                      {!readOnly ? (
                        <AwpCloseCancelButtons
                          props={props}
                          isPristine={!props.dirty || props.isSubmitting}
                          hasAccess={true}
                          onConfirmedCancel={() => {
                            onClickCancel();
                          }}
                        />
                      ) : (
                        <Authorize requires={[Permissions.WriteAdminTools]}>
                          <div className="mr-3">
                            <Button variant="outline-primary" onClick={() => onClickCancel()}>
                              {!props.dirty ? 'Close' : 'Cancel'}
                            </Button>
                            <Button className="ml-2" variant="outline-primary" onClick={onEditButtonClick}>
                              Edit Avalanche Path
                            </Button>
                          </div>
                        </Authorize>
                      )}
                    </Card.Footer>
                  </Card>
                </Form>
              )}
            </Formik>
            <AwpValidationErrorModal setErrors={setEntityErrors} errors={entityErrors} />
          </>
        )}
      </AwpContainer>
    </>
  );
};

export default AvalanchePathsView;
